import React, { useState } from 'react';
import {
  ChevronDownIcon, ChevronUpIcon,
  ClipboardDocumentIcon, CheckIcon,
} from '@heroicons/react/20/solid';
import CodeSnippetView from './CodeSnipperView';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTime(ts) {
  if (!ts) return '—';
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtPrice(p) {
  if (p == null) return '—';
  return `$${Number(p).toFixed(5)}`;
}

function capitalize(s) {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      title="Copy"
      className="ml-1.5 inline-flex items-center text-gray-300 dark:text-gray-600 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors"
    >
      {copied
        ? <CheckIcon className="w-3.5 h-3.5 text-green-500" />
        : <ClipboardDocumentIcon className="w-3.5 h-3.5" />}
    </button>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────

function Section({ title, defaultOpen = true, icon, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
          {icon && <span>{icon}</span>}
          {title}
        </span>
        {open
          ? <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="bg-white dark:bg-black">{children}</div>}
    </div>
  );
}

// ── Label/value row ───────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0 px-4">
      <span className="text-xs text-gray-400 dark:text-gray-500 w-24 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-gray-800 dark:text-gray-200 font-medium break-all">{children}</span>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status, terminal }) {
  if (!status) return <span className="text-gray-400">—</span>;
  const base = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold';
  if (terminal)
    return <span className={`${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>✓ {status}</span>;
  return <span className={`${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`}>✓ {status}</span>;
}

// ── Response timeline ─────────────────────────────────────────────────────────

function responseLabel(r) {
  if ('ErrorMessasge' in r || 'errorCode' in r) return 'Response Logged';
  if (r.channel) return `${r.channel} Sent`;
  return r.MessageStatus ?? r.status ?? 'Response';
}

function ResponseCard({ r, isLast }) {
  const label = responseLabel(r);
  const time  = fmtTime(r.insertDTTM);

  const isLogEntry  = 'ErrorMessasge' in r;
  const isVendorMsg = r.channel != null;

  return (
    <div className="flex gap-3">
      {/* timeline spine */}
      <div className="flex flex-col items-center">
        <div className="w-3 h-3 rounded-full bg-indigo-500 flex-shrink-0 mt-1" />
        {!isLast && <div className="w-0.5 flex-1 bg-indigo-200 dark:bg-indigo-900 mt-1" />}
      </div>

      {/* card */}
      <div className="flex-1 mb-4 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-600 dark:text-gray-400">
          {time} — {label}
        </div>
        <div className="bg-white dark:bg-black divide-y divide-gray-100 dark:divide-gray-800">
          {isLogEntry && r.vendorMessageId && (
            <div className="px-4 py-2 text-xs font-mono text-gray-500 dark:text-gray-400 flex items-center">
              vendorMessageId:&nbsp;
              <span className="text-indigo-600 dark:text-indigo-400">{r.vendorMessageId}</span>
              <CopyButton value={r.vendorMessageId} />
            </div>
          )}

          {isVendorMsg && (
            <>
              {r.from  && <Field label="From">{r.from}</Field>}
              {r.to    && <Field label="To">{r.to}</Field>}
              {r.status && (
                <Field label="Status">
                  <StatusBadge status={r.status} terminal={false} />
                </Field>
              )}
              {r.usage && (() => {
                const m = r.usage.match(/price=([^,)]+)/);
                return m ? <Field label="Price">{fmtPrice(m[1])}</Field> : null;
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MessageLogView({ data }) {
  const req       = data.requestMessage ?? {};
  const connector = data.smsConnector   ?? {};
  const responses = data.responses      ?? [];

  return (
    <div className="space-y-4">

      {/* ── Header cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        {/* Message ID */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-black">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Message ID</p>
          <p className="font-mono text-sm text-gray-800 dark:text-gray-200 break-all flex items-start">
            {data.mvId ?? '—'}
            {data.mvId && <CopyButton value={data.mvId} />}
          </p>
        </div>

        {/* Status */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-black flex flex-col items-center justify-center gap-1">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Status</p>
          <StatusBadge status={data.status} terminal={data.terminal} />
        </div>

        {/* Direction + Price */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-black divide-y divide-gray-100 dark:divide-gray-800">
          <div className="p-4">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Direction</p>
            <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{capitalize(data.direction)}</p>
          </div>
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Price</p>
            <p className="text-sm text-gray-800 dark:text-gray-200 font-semibold">{fmtPrice(data.price)}</p>
          </div>
        </div>
      </div>

      {/* ── Request + Connector ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Section title="Request Details">
          <Field label="From">{req.fromNumber ?? '—'}</Field>
          <Field label="To">{req.toNumber ?? '—'}</Field>
          <Field label="Message">{req.message ?? '—'}</Field>
          <Field label="Channel">{req.channel ?? '—'}</Field>
          <Field label="Priority">{req.priority ?? '—'}</Field>
        </Section>

        <Section title="Connector Info">
          <Field label="Vendor">{connector.vendor ?? '—'}</Field>
          <Field label="Testing Mode">
            {connector.testing
              ? <span className="text-green-600 dark:text-green-400 font-semibold">Enabled</span>
              : <span className="text-gray-500">Disabled</span>}
          </Field>
        </Section>
      </div>

      {/* ── Response Timeline ── */}
      {responses.length > 0 && (
        <Section title="Response Timeline">
          <div className="px-4 pt-4">
            {responses.map((r, i) => (
              <ResponseCard key={i} r={r} isLast={i === responses.length - 1} />
            ))}
          </div>
        </Section>
      )}

      {/* ── Raw JSON ── */}
      <Section title="Raw JSON Data" defaultOpen={false} icon="⊞">
        <div className="p-4">
          <CodeSnippetView data={data} />
        </div>
      </Section>
    </div>
  );
}
